import React, { useCallback } from 'react';
import styled from 'styled-components';
import { layout, media } from 'src/components/base';

import {
  H4Text,
  BodySmallText,
  TitleMediumText,
  FontColor,
} from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';

// TODO Add equivalent icon to DS and use here
import HistoryIconComponent from 'mdi-react/HistoryIcon';

import { Clickable } from 'src/components/clickable';

import { ListItem, ListItemButton } from './styles';

import { useAppContext } from 'src/components/app-context';

import {
  trackSearchHistoryClicked,
  trackSearchHistoryDeleted,
} from '../../tracking';
import { IconClose } from '@prisjakt/ui/icon/close';

const ORDER = 100;

type Props = {
  text: string;
  selected: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClear: () => void;
  showClear: boolean;
};

export const HistoryItem = (props: Props) => {
  const { text, onDelete, onClick, selected, onClear, showClear } = props;

  const { t } = useAppContext();

  const handleOnClick = useCallback(() => {
    onClick();
    trackSearchHistoryClicked(text);
  }, [onClick, text]);

  const handleOnDelete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onDelete(event);
      trackSearchHistoryDeleted(text);
    },
    [onDelete, text]
  );

  return (
    <>
      {showClear ? (
        <ClearButton color={FontColor.PRIMARY}>
          <Clickable onClick={onClear} data-test="ClearHistoryItemButton">
            {t('common', 'button.clear')}
          </Clickable>
        </ClearButton>
      ) : null}

      <ListItem data-test="HistoryItem" $selected={selected} order={ORDER}>
        <ListItemButton onClick={handleOnClick}>
          <ItemInner gap={0.5}>
            <HistoryIcon size={iconSize} />

            <Text>{text}</Text>
          </ItemInner>
        </ListItemButton>

        <RemoveButton onClick={handleOnDelete}>
          <IconClose size={12} color="base600" />
        </RemoveButton>
      </ListItem>
    </>
  );
};

const { spacing050, spacing100 } = layout.measurements;

const iconSize = 24;

const ItemInner = styled(Row)`
  align-items: center;
  padding: ${spacing050} 0;
  overflow: hidden;
  margin-right: ${iconSize}px;
`;

const RemoveButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.bg.secondary};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 150ms ease;
  color: ${p => p.theme.icons.primary};
  position: absolute;
  top: 50%;
  right: ${layout.measurements.spacing100};
  transform: translateY(-50%);

  &:hover {
    background-color: ${p => p.theme.bg.tertiary};
  }
`;

const HistoryIcon = styled(HistoryIconComponent)`
  color: ${p => p.theme.icons.tertiary};
  min-width: ${iconSize}px;
`;

export const Title = styled(H4Text).attrs({ 'data-test': 'HistoryItemsTitle' })`
  order: ${ORDER};
  margin-left: ${spacing100};
  margin-top: ${spacing100};
  flex: 0 1 auto;
`;

const ClearButton = styled(TitleMediumText)`
  flex: 0 1 auto;
  order: ${ORDER + 1};
  margin-left: ${spacing100};
  margin-top: ${spacing100};

  ${media.mediumUp`
    display: none;
  `};

  button {
    color: inherit;
    cursor: pointer;
  }
`;

const Text = styled(BodySmallText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
